import React, { useEffect, useMemo, useState } from "react";

import { Grid, SelectChangeEvent } from "@mui/material";
import { MenuItem } from "@mui/material";
import { Input, Select } from "Components/Form";
import RadioButton from "Components/Form/RadioGroup";
import SkeletonComponent from "Components/Generic/Skeleton";
import { add, isBefore, isSameDay } from "date-fns";
import useYupValidation from "Hooks/useYupValidation";
import { Company } from "Models/company";
import { Task, TaskDateProperties, TaskStartDatePeriod } from "Models/task";
import { TaskType } from "Models/task.type";
import { useTranslation } from "react-i18next";
import { getStartDatePeriods, getStartDateRangeLabel } from "Util/startDate";
import * as yup from "yup";
import { PERIOD_BUFFER_TIME } from "Constants";
import { TimeSlotType } from "Util/enums";
import { TimeSlotTask } from "Models/time.slot.task";
import { VariableSlotTime } from "../../../pages/private/Moving/Operations/Maintenance/Tasks/VariableSlotTime";
import { TaskDateUtils } from "Util/dateUtils";
import { useTaskDateManagement } from "Hooks/useTaskDateManagement";
import { useAppState } from "Context/StateContext";

export interface TaskForm {
  taskName?: string;
  startDate: string;
  endDate: string;
  type: string;
  period: TaskStartDatePeriod;
  description?: string;
  timeSlots?: TimeSlotTask[];
  startTime?: string;
  endTime?: string;
}

interface UseCreateTaskFormProps {
  errors: yup.ValidationError[];
  isLoadingTypes?: boolean;
  isLoading?: boolean;
  taskTypes?: TaskType[];
  companies?: Company[];
  isLoadingCompanies?: boolean;
  showDescription?: boolean;
  defaultCompany?: Company;
  isPlaceholderTask?: boolean;
  currentDate?: Date;
}

export const useCreateTaskForm = ({
  errors,
  isLoadingTypes = false,
  isLoading,
  taskTypes,
  companies,
  isLoadingCompanies = false,
  showDescription = false,
  defaultCompany,
  currentDate,
}: UseCreateTaskFormProps) => {
  const { t: translate } = useTranslation();
  const { getErrorByField } = useYupValidation(errors);
  const { currentUser } = useAppState();

  const timezone = currentUser?.timezone?.name || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const defaultTask: Partial<Task> = {
    name: "",
    startDate: undefined,
    endDate: undefined,
    description: "",
    period: TaskStartDatePeriod.MORNING,
  };
  const [task, setTask] = useState<Partial<Task>>(defaultTask);

  const {
    isMultiDayTask,
    handlePeriodChange: handleChangePeriod,
    startDateInput: startDateLabel,
    endDateInput: endDateLabel,
    startTimeInput: startTime,
    endTimeInput: endTime,
  } = useTaskDateManagement({ initialTask: task as Task, onTaskUpdate: setTask });

  useEffect(() => {
    if (!task.endDate || !task.startDate) return;

    let taskEndDate = new Date(task.endDate);
    const taskStartDate = new Date(task.startDate);

    if (
      isBefore(taskEndDate, taskStartDate) ||
      ([
        TaskStartDatePeriod.MULTI_DAY,
        TaskStartDatePeriod.VARIABLE_TIME,
        TaskStartDatePeriod.FIXED_TIME,
      ].includes(task?.period!) &&
        isSameDay(taskEndDate, taskStartDate))
    ) {
      if (!task?.period) return;

      taskEndDate = new Date(taskStartDate);
      taskEndDate = add(taskEndDate, PERIOD_BUFFER_TIME[task?.period as TaskStartDatePeriod]);

      updateTaskField("endDate", taskEndDate.toISOString());
    }
  }, [task.period, task.startDate, task.endDate]);

  const updateTaskField = <T extends keyof Task>(key: T, value: Task[T]) => {
    setTask((prevTask) => ({ ...prevTask, [key]: value }));
  };

  const handleChangeDateTime = (key: TaskDateProperties, value: string) => {
    const oldDate = task[key] ?? task.startDate;
    const existingTime = oldDate ? TaskDateUtils.extractTimeForInput(oldDate!) : undefined;

    const date = TaskDateUtils.createTaskDateTime(
      {
        dateInput: value,
        timeInput: existingTime,
        preserveExistingTime: true,
        existingDateString: task[key],
      },
      timezone
    );

    updateTaskField(key, date);
  };

  const handleChangeTime = (key: TaskDateProperties, value: string) => {
    const currentStartDate = task[key] ?? task.startDate;
    const existentDate = currentStartDate
      ? TaskDateUtils.extractDateForInput(currentStartDate)
      : TaskDateUtils.getTodayForInput();
    const date = TaskDateUtils.createTaskDateTime(
      { dateInput: existentDate, timeInput: value },
      timezone
    );

    updateTaskField(key, date);
  };

  const [taskTypeSelected, setTaskTypeSelected] = useState<string>("");
  const [companySelected, setCompanySelected] = useState<Company>();
  const [slotTime, setSlotTime] = useState<TimeSlotType>(TimeSlotType.NONE);

  useEffect(() => {
    setCompanySelected(defaultCompany);
  }, [defaultCompany]);

  const clearAllFields = () => {
    setTask(defaultTask);
    setTaskTypeSelected("");
    setSlotTime(TimeSlotType.NONE);
    setCompanySelected(defaultCompany);
  };

  const defaultTimePeriods = useMemo(() => getStartDatePeriods(new Date()), []);
  const preferences = currentUser?.preferences;

  const getPeriodLabel = (periodKey: keyof ReturnType<typeof getStartDatePeriods>, options = {}) =>
    getStartDateRangeLabel(defaultTimePeriods[periodKey], { ...options, preferences });

  const renderSelectPeriod = () => (
    <Grid item xs={6}>
      <Select
        required
        label={translate("period")}
        placeholder={translate("selectOne")}
        value={task.period}
        onChange={(event) =>
          handleChangePeriod({ period: event.target.value as TaskStartDatePeriod, currentDate })
        }
        disabled={isLoading}
      >
        <MenuItem value={TaskStartDatePeriod.MORNING}>
          {`${translate("morning")} (${getPeriodLabel("morning")})`}
        </MenuItem>
        <MenuItem value={TaskStartDatePeriod.AFTERNOON}>
          {`${translate("afternoon")} (${getPeriodLabel("afternoon")})`}
        </MenuItem>
        <MenuItem value={TaskStartDatePeriod.EVENING}>
          {`${translate("evening")} (${getPeriodLabel("evening", { considerEvening: true })})`}
        </MenuItem>
        <MenuItem value={TaskStartDatePeriod.FULL_DAY}>
          {`${translate("fullDay")} (${getPeriodLabel("fullDay", { isFullDay: true })})`}
        </MenuItem>
        <MenuItem value={TaskStartDatePeriod.CUSTOM}>{translate("custom")}</MenuItem>
      </Select>
    </Grid>
  );

  const renderMultiDayRadio = () => (
    <Grid item xs={isMultiDayTask ? 3 : 12}>
      <RadioButton
        label={translate("isThisAMultiDayTask")}
        disabled={isLoading}
        onChange={(event) => {
          setSlotTime(TimeSlotType.NONE);
          setTask((prev) => ({ ...(prev || {}), timeSlots: [] } as Task));

          const rawValue = event.target.value;

          const parsedValue = rawValue.includes(",")
            ? rawValue.split(",").map((value) => Number(value.trim()))
            : Number(rawValue);

          handleChangePeriod({
            period:
              (Array.isArray(parsedValue) ? parsedValue.at(0) : parsedValue) ??
              TaskStartDatePeriod.MORNING,
            currentDate,
          });
        }}
        options={[
          {
            label: translate("yes"),
            value: [
              TaskStartDatePeriod.MULTI_DAY,
              TaskStartDatePeriod.VARIABLE_TIME,
              TaskStartDatePeriod.FIXED_TIME,
            ],
          },
          {
            label: translate("no"),
            value: [
              TaskStartDatePeriod.MORNING,
              TaskStartDatePeriod.AFTERNOON,
              TaskStartDatePeriod.EVENING,
              TaskStartDatePeriod.FULL_DAY,
              TaskStartDatePeriod.CUSTOM,
            ],
          },
        ]}
        value={task.period}
      />
    </Grid>
  );

  const renderTaskTimeSlotRadio = () => (
    <Grid item xs={7}>
      <RadioButton
        label={translate("isTimeSlotRequiredForMultiDayTask")}
        onChange={(event) => {
          if (event.target.value === TimeSlotType.VARIABLE) {
            setTask(
              (prev) =>
                ({
                  ...(prev || {}),
                  period: TaskStartDatePeriod.VARIABLE_TIME,
                  timeSlots: [
                    {
                      date: TaskDateUtils.createTaskDateTime(
                        {
                          dateInput: TaskDateUtils.getTodayForInput(currentDate, { timezone }),
                          timeInput: "09:00",
                        },
                        timezone
                      ),
                    } as TimeSlotTask,
                  ],
                } as Task)
            );
          } else {
            const taskPeriod =
              event.target.value === TimeSlotType.FIXED
                ? TaskStartDatePeriod.FIXED_TIME
                : TaskStartDatePeriod.MULTI_DAY;
            setTask((prev) => ({ ...(prev || {}), period: taskPeriod, timeSlots: [] } as Task));
          }

          setSlotTime(event.target.value as TimeSlotType);
        }}
        value={slotTime}
        options={[
          { label: translate("no"), value: TimeSlotType.NONE },
          {
            label: translate("fixedTimeSlot"),
            value: TimeSlotType.FIXED,
          },
          {
            label: translate("variableTimeSlot"),
            value: TimeSlotType.VARIABLE,
          },
        ]}
      />
    </Grid>
  );

  const form = (
    <Grid container item spacing={2}>
      {companies?.length || isLoadingCompanies ? (
        <Grid container item xs={isMultiDayTask ? 6 : 12}>
          <Grid item xs={isMultiDayTask ? 12 : 6}>
            <SkeletonComponent isLoading={isLoadingCompanies} isInput>
              <Select
                required
                label={translate("company")}
                placeholder={translate("selectOne")}
                value={companySelected?.id}
                onChange={(event) => {
                  const companyPicked = companies?.find(
                    (company) => company.id === Number(event.target.value)
                  );

                  if (companyPicked) {
                    setCompanySelected(companyPicked);
                  }
                }}
                disabled={isLoadingCompanies}
                error={!!getErrorByField("company")}
                errorMessage={getErrorByField("company")}
              >
                {companies?.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </SkeletonComponent>
          </Grid>
        </Grid>
      ) : null}

      {taskTypes?.length || isLoadingTypes ? (
        <Grid item xs={isMultiDayTask ? 12 : 6}>
          <SkeletonComponent isLoading={isLoadingTypes} isInput>
            <Select
              required
              label={translate("task")}
              placeholder={translate("selectOne")}
              value={taskTypeSelected}
              onChange={(event: SelectChangeEvent<any>) => setTaskTypeSelected(event.target.value)}
              disabled={isLoading}
              error={!!getErrorByField("taskType")}
              errorMessage={getErrorByField("taskType")}
            >
              {taskTypes?.map((taskType) => (
                <MenuItem key={taskType.id} value={taskType.name}>
                  {taskType.name}
                </MenuItem>
              ))}
            </Select>
          </SkeletonComponent>
        </Grid>
      ) : (
        <Grid item xs={6}>
          <Input
            type="text"
            required
            label={translate("taskName")}
            placeholder={translate("taskName")}
            value={task.name}
            onChange={(event) => {
              updateTaskField("name", event.target.value);
            }}
            disabled={isLoading}
            error={!!getErrorByField("taskName")}
            errorMessage={getErrorByField("taskName")}
          />
        </Grid>
      )}

      <Grid container item xs={isMultiDayTask ? 12 : 4}>
        {renderMultiDayRadio()}
        {isMultiDayTask && renderTaskTimeSlotRadio()}
      </Grid>

      {!isMultiDayTask && renderSelectPeriod()}

      {slotTime !== TimeSlotType.VARIABLE ? (
        <>
          <Grid item xs={6}>
            <Input
              type="date"
              required
              label={translate(isMultiDayTask ? "startDate" : "date")}
              min={new Date().toISOString().split("T")[0]}
              placeholder=""
              value={startDateLabel}
              onChange={(event) => {
                handleChangeDateTime("startDate", event.target.value);
              }}
              disabled={isLoading}
              error={!!getErrorByField("startDate")}
              errorMessage={getErrorByField("startDate")}
            />
          </Grid>

          {isMultiDayTask && (
            <Grid item xs={6}>
              <Input
                type="date"
                required
                label={translate("endDate")}
                min={add(new Date(task?.startDate!), { days: 1 })}
                placeholder=""
                value={endDateLabel}
                onChange={(event) => {
                  handleChangeDateTime("endDate", event.target.value);
                }}
                disabled={isLoading}
                error={!!getErrorByField("endDate")}
                errorMessage={getErrorByField("endDate")}
              />
            </Grid>
          )}
          {(!isMultiDayTask || (isMultiDayTask && slotTime === TimeSlotType.FIXED)) && (
            <>
              <Grid item xs={6}>
                <Input
                  type="time"
                  required
                  label={translate("from")}
                  placeholder="00:00 AM"
                  value={startTime}
                  onChange={(event) => handleChangeTime("startDate", event.target.value)}
                  disabled={
                    isLoading || (!isMultiDayTask && task.period !== TaskStartDatePeriod.CUSTOM)
                  }
                  error={!!getErrorByField("startTime")}
                  errorMessage={getErrorByField("startTime")}
                />
              </Grid>

              <Grid item xs={6}>
                <Input
                  type="time"
                  required
                  label={translate("to")}
                  placeholder="00:00 AM"
                  value={endTime}
                  onChange={(event) => handleChangeTime("endDate", event.target.value)}
                  disabled={
                    isLoading || (!isMultiDayTask && task.period !== TaskStartDatePeriod.CUSTOM)
                  }
                  error={!!getErrorByField("endTime")}
                  errorMessage={getErrorByField("endTime")}
                />
              </Grid>
            </>
          )}
        </>
      ) : (
        <VariableSlotTime
          task={task as Task}
          setTask={(item) => {
            setTask((prev) => ({ ...(prev || {}), ...(item || {}) } as Task));
          }}
          errors={errors}
          currentDate={currentDate}
        />
      )}
      {showDescription ? (
        <Grid item xs={12}>
          <Input
            type="text"
            multiline
            minRows={2}
            label={translate("taskDescription")}
            placeholder={translate("describeTaskHere")}
            value={task.description}
            onChange={(event) => {
              updateTaskField("description", event.target.value);
            }}
            disabled={isLoading}
            error={!!getErrorByField("description")}
            errorMessage={getErrorByField("description")}
          />
        </Grid>
      ) : null}
    </Grid>
  );

  return {
    form,
    companySelected,
    task: {
      taskName: task.name,
      startTime,
      endTime,
      startDate: task.startDate,
      endDate: task.endDate,
      type: taskTypeSelected,
      period: task.period,
      timeSlots: task.timeSlots,
    } as TaskForm,
    description: task.description,
    isMultiDayTask,
    clearAllFields,
    handleChangeDateTime,
    setTaskTypeSelected,
    handleChangePeriod,
  };
};
